import { createFileRoute, Navigate, Outlet, redirect } from '@tanstack/react-router'
import React from 'react'

import { ConsentReconfirmGate } from '#components/ConsentReconfirmGate'
import { useAuth } from '#hooks/useAuth'
import { getHighestRole, ROLE_USER } from '#interfaces/auth'
import { FullLayout } from '#layouts/FullLayout'
import { ensureSession } from '#lib/session'
import { getToken } from '#lib/tokenStore'
import { UserNavProvider } from '#providers/UserNavProvider'

function AuthenticatedLayout(): React.JSX.Element {
  const { isAuthenticated, user } = useAuth()

  // Safety net: if token was invalid and auth initialization cleared it,
  // beforeLoad already passed but user is gone — redirect to login.
  if (!isAuthenticated) return <Navigate to="/login" />

  const highestRole = getHighestRole(user?.roles)
  const content = <Outlet />

  // ROLE_USER needs UserNavProvider so UserDashboard and UserSideNav can access user context
  if (highestRole === ROLE_USER && user) {
    return (
      <FullLayout>
        <UserNavProvider
          userId={user.id}
          userName={user.displayName ?? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()}
          companyId={user.companyId ?? ''}
          companyName={user.companyName ?? ''}
        >
          <ConsentReconfirmGate>{content}</ConsentReconfirmGate>
        </UserNavProvider>
      </FullLayout>
    )
  }

  return <FullLayout>{content}</FullLayout>
}

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async (): Promise<void> => {
    await ensureSession()
    if (!getToken()) throw redirect({ to: '/login' })
  },
  component: AuthenticatedLayout,
})
